import { supabase } from "@/integrations/supabase/client";
import { PortfolioItem, Portfolio } from "./types";

/**
 * Fetch the user's portfolio
 */
export const fetchUserPortfolio = async (): Promise<Portfolio | null> => {
  const { data: portfolios, error } = await supabase
    .from('portfolios')
    .select('*')
    .single();

  if (error) {
    console.error('Error fetching portfolio:', error);
    return null;
  }

  return portfolios;
};

/**
 * Fetch all portfolio items for the current user
 */
export const fetchPortfolioItems = async (): Promise<PortfolioItem[]> => {
  const { data: portfolio, error: portfolioError } = await supabase
    .from('portfolios')
    .select('id')
    .single();

  if (portfolioError) {
    console.error('Error fetching portfolio:', portfolioError);
    return [];
  }

  const { data: items, error } = await supabase
    .from('portfolio_items')
    .select('*')
    .eq('portfolio_id', portfolio.id)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching portfolio items:', error);
    return [];
  }

  // Type cast to ensure compatibility
  return items as PortfolioItem[];
};

/**
 * Fetch portfolio items filtered by taxonomy level
 */
export const fetchPortfolioItemsByTaxonomy = async (
  taxonomyLevel: string
): Promise<PortfolioItem[]> => {
  const { data: portfolio, error: portfolioError } = await supabase
    .from('portfolios')
    .select('id')
    .single();

  if (portfolioError) {
    console.error('Error fetching portfolio:', portfolioError);
    return [];
  }

  const { data: items, error } = await supabase
    .from('portfolio_items')
    .select('*')
    .eq('portfolio_id', portfolio.id)
    .eq('taxonomy_level', taxonomyLevel)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching portfolio items:', error);
    return [];
  }

  // Type cast to ensure compatibility
  return items as PortfolioItem[];
};

/**
 * Create a new portfolio item
 */
export const createPortfolioItem = async (
  itemData: Omit<PortfolioItem, 'id' | 'portfolio_id' | 'created_at' | 'updated_at'>
): Promise<PortfolioItem | null> => {
  const { data: portfolio, error: portfolioError } = await supabase
    .from('portfolios')
    .select('id')
    .single();

  if (portfolioError) {
    console.error('Error fetching portfolio:', portfolioError);
    return null;
  }

  const { data, error } = await supabase
    .from('portfolio_items')
    .insert({
      ...itemData,
      portfolio_id: portfolio.id
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating portfolio item:', error);
    return null;
  }

  return data as PortfolioItem;
};

/**
 * Update an existing portfolio item
 */
export const updatePortfolioItem = async (
  id: string,
  itemData: Partial<Omit<PortfolioItem, 'id' | 'portfolio_id' | 'created_at' | 'updated_at'>>
): Promise<PortfolioItem | null> => {
  const { data, error } = await supabase
    .from('portfolio_items')
    .update(itemData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating portfolio item:', error);
    return null;
  }

  return data as PortfolioItem;
};

/**
 * Delete a portfolio item
 */
export const deletePortfolioItem = async (id: string): Promise<boolean> => {
  const { error } = await supabase
    .from('portfolio_items')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting portfolio item:', error);
    return false;
  }

  return true;
};

/**
 * Upload a file to the portfolio media bucket
 */
export const uploadPortfolioMedia = async (
  file: File,
  fileName?: string
): Promise<string | null> => {
  const user = supabase.auth.getUser();
  const userId = (await user).data.user?.id;
  
  if (!userId) {
    console.error('User not authenticated');
    return null;
  }

  const fileExt = file.name.split('.').pop();
  const fileKey = fileName ? 
    `${userId}/${fileName}.${fileExt}` : 
    `${userId}/${Math.random().toString(36).substring(2)}.${fileExt}`;

  const { data, error } = await supabase.storage
    .from('portfolio_media')
    .upload(fileKey, file, {
      cacheControl: '3600',
      upsert: false
    });

  if (error) {
    console.error('Error uploading file:', error);
    return null;
  }

  // Get the public URL for the file
  const { data: { publicUrl } } = supabase.storage
    .from('portfolio_media')
    .getPublicUrl(data.path);

  return publicUrl;
};
